// src/pages/Intro.js
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Confetti from "react-confetti";
import VirtualPiano from "../components/VirtualPiano";
import RealPianoListener from "../components/RealPianoListener";
import FindTheNoteGame from "../components/FindTheNoteGame";
import OverworldLightbox from "../components/OverworldLightbox";
import PianoViewport from "../components/PianoViewport";

const basicNotes = ["C", "D", "E", "F", "G", "A", "B"];
const totalNotes = basicNotes.length;

/** Slides:
 * Last slide is a big CTA text instead of an image.
 */
const introSlides = [
  { type: "story", image: "/images/Intro/treble-town-welcome.png" },
  { type: "story", image: "/images/Intro/Slide 2.png" },
  { type: "story", image: "/images/Intro/Intro-Slide3-Doron.png" },
  {
    type: "notes",
    image: "/images/Intro/Piano Roll.png",
    text:
      "First, learn your 7 white keys. Find the two black keys and the white key to the left of them is always the note C.",
  },
  {
    type: "octaveMap",
    image: "/images/Intro/octave_map.png",
    text:
      "This is your keyboard map. The middle octave is between C4 and C5. For Treble Town, we’ll use these white keys: C D E F G A B.",
  },
  {
    type: "cta",
    headline:
      "Now use your own piano and play the seven white notes to unlock Chapter 1!",
    sub: "Play them in order: C  D  E  F  G  A  B",
  },
];

/* ===== Modal ===== */
function PanWisIntroModal({ open, onClose, onGoChapter1, onOpenMap }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[1000] bg-black/40 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl rounded-2xl bg-white shadow-xl p-6">
        <div className="flex gap-4">
          <img src="/images/pan-the-panda.png" alt="Pan" className="w-20 h-20 object-contain" />
          <img
            src="/images/Wis_.png"
            alt="Wis"
            className="w-20 h-20 object-contain"
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = "/images/wis.png";
            }}
          />
          <div>
            <h2 className="text-xl font-bold">Great job—welcome to Treble Town!</h2>
            <p className="text-gray-700 mt-2">
              I’m <strong>Pan the Panda</strong> and this is <strong>Wis</strong>. Doron waits on the highest hill.
              There’s a <strong>magic melody</strong> that can make him fall asleep—let’s begin our quest in Chapter 1!
            </p>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onOpenMap} className="px-4 py-2 rounded-lg border">Open Map</button>
          <button onClick={onGoChapter1} className="px-4 py-2 rounded-lg bg-amber-400 hover:bg-amber-300 font-medium">
            Go to Chapter 1
          </button>
          <button onClick={onClose} className="px-4 py-2 rounded-lg">Close</button>
        </div>
      </div>
    </div>
  );
}

export default function Intro() {
  const navigate = useNavigate();
  const noteClickHandlerRef = useRef(null);
  const listenSectionRef = useRef(null);

  const [currentSlide, setCurrentSlide] = useState(0);
  const [detectedNote, setDetectedNote] = useState(null);
  const [melodyCompleted, setMelodyCompleted] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [showPanWis, setShowPanWis] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);

  // progress 0/7 tracker
  const [progress, setProgress] = useState(0);

  // Narration (default ON)
  const [narrationOn, setNarrationOn] = useState(true);
  const [voices, setVoices] = useState([]);
  const [voiceIndex, setVoiceIndex] = useState(0);
  const autoPickedVoiceRef = useRef(false);
  const [ttsPrimed, setTtsPrimed] = useState(false);

  // Small-screen portrait hint
  const [showTiltHint, setShowTiltHint] = useState(false);
  const [hintDismissed, setHintDismissed] = useState(false);

  // Narration text per slide (keep your edits)
  const narrationBySlide = [
    "Welcome to Treble Town!",
    "Doron the Dragon is attacking, you have to help us, learn your notes quick!!.",
    "Meet Doron on the highest hill.",
    "But, First, learn your seven white keys. The white key to the left of two black keys is always C.",
    "Find the middle octave, between C four and C five. We will use the white keys C, D, E, F, G, A, and B.",
    "Now use your own piano and play the seven white notes in order to unlock Chapter One — C, D, E, F, G, A, B.",
  ];

  /** Prefer British English female voice:
   * 1) "Google UK English Female"
   * 2) en-GB with female-ish names or "female" hint
   * 3) any en-GB
   * 4) any English
   */
  const pickUKFemaleVoiceIndex = (list) => {
    if (!list || !list.length) return 0;
    const lc = (s) => (s || "").toLowerCase();
    const isGB = (v) =>
      (v.lang && lc(v.lang).startsWith("en-gb")) || (v.lang && lc(v.lang) === "en_gb");
    const femaleHints = [
      "female", "kate", "serena", "amelia", "sophie", "olivia", "emma", "alice",
      "charlotte", "lucy", "isabella", "grace", "megan", "ellie", "lily"
    ];

    // 1) exact Google UK English Female
    for (let i = 0; i < list.length; i++) {
      const n = lc(list[i].name || "");
      if (n.includes("google uk english female")) return i;
    }
    // 2) en-GB + female-ish name
    for (let i = 0; i < list.length; i++) {
      const v = list[i];
      if (isGB(v)) {
        const n = lc(v.name || "");
        if (femaleHints.some((h) => n.includes(h))) return i;
      }
    }
    // 3) any en-GB
    for (let i = 0; i < list.length; i++) if (isGB(list[i])) return i;
    // 4) any English
    for (let i = 0; i < list.length; i++) {
      if ((list[i].lang || "").toLowerCase().startsWith("en")) return i;
    }
    return 0;
  };

  // Prime TTS after first gesture (required by browsers)
  useEffect(() => {
    const prime = () => setTtsPrimed(true);
    document.addEventListener("pointerdown", prime, { once: true });
    document.addEventListener("keydown", prime, { once: true });
    return () => {
      document.removeEventListener("pointerdown", prime);
      document.removeEventListener("keydown", prime);
    };
  }, []);

  // Load voices and auto-pick a UK English female voice
  useEffect(() => {
    const synth = window.speechSynthesis;
    if (!synth) return;

    const load = () => {
      const v = synth.getVoices() || [];
      setVoices(v);
      if (!autoPickedVoiceRef.current && v.length) {
        const idx = pickUKFemaleVoiceIndex(v);
        setVoiceIndex(idx);
        autoPickedVoiceRef.current = true;
      }
    };
    load();
    synth.onvoiceschanged = load;
    return () => {
      synth.onvoiceschanged = null;
    };
  }, []);

  // Speak helper
  const speak = (text) => {
    const synth = window.speechSynthesis;
    if (!synth || !text || !narrationOn || !ttsPrimed) return;
    synth.cancel();
    const u = new SpeechSynthesisUtterance(text);
    const voice = voices[Math.min(voiceIndex, Math.max(0, voices.length - 1))];
    if (voice) {
      u.voice = voice;
      u.lang = voice.lang || "en-GB";
    } else {
      u.lang = "en-GB";
    }
    u.rate = 0.95;
    u.pitch = 1.0;
    synth.speak(u);
  };

  // Auto-read when slide changes (default ON)
  useEffect(() => {
    speak(narrationBySlide[currentSlide]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSlide, narrationOn, ttsPrimed, voiceIndex, voices.length]);

  useEffect(() => {
    if (hintDismissed) return;
    const mq = window.matchMedia("(orientation: portrait)");
    const check = () => {
      const small = window.innerWidth < 480;
      setShowTiltHint(small && mq.matches);
    };
    check();
    window.addEventListener("resize", check);
    mq.addEventListener?.("change", check);
    return () => {
      window.removeEventListener("resize", check);
      mq.removeEventListener?.("change", check);
    };
  }, [hintDismissed]);

  // Mobile mic preflight
  const isSecure = typeof window !== "undefined" && window.isSecureContext;
  const isMobileUA =
    typeof navigator !== "undefined" && /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
  const [micPrimed, setMicPrimed] = useState(!isMobileUA);
  const [micMsg, setMicMsg] = useState("");

  useEffect(() => {
    if (!isMobileUA) return;

    let done = false;
    const preflight = async () => {
      if (done) return;
      try {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (AC) {
          const ctx = new AC();
          if (ctx.state === "suspended") await ctx.resume();
          if (ctx.close) await ctx.close();
        }
        if (!isSecure) {
          setMicMsg("Mic requires HTTPS on mobile (use https dev URL for testing).");
          setMicPrimed(true);
          done = true;
          cleanup();
          return;
        }
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: false },
        });
        stream.getTracks().forEach((t) => t.stop());
        setMicPrimed(true);
        done = true;
        cleanup();
      } catch (e) {
        setMicMsg((e && e.message) || "Microphone permission failed.");
        setMicPrimed(true);
        done = true;
        cleanup();
      }
    };

    const handler = () => preflight();
    document.addEventListener("pointerdown", handler, { once: true });
    document.addEventListener("click", handler, { once: true });
    document.addEventListener("touchstart", handler, { once: true });

    const cleanup = () => {
      document.removeEventListener("pointerdown", handler);
      document.removeEventListener("click", handler);
      document.removeEventListener("touchstart", handler);
    };
    return cleanup;
  }, [isMobileUA, isSecure]);

  const isLastSlide = currentSlide === introSlides.length - 1;

  useEffect(() => {
    if (isLastSlide && listenSectionRef.current) {
      listenSectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [isLastSlide]);

  useEffect(() => {
    if (showConfetti) {
      const t = setTimeout(() => setShowConfetti(false), 4000);
      return () => clearTimeout(t);
    }
  }, [showConfetti]);

  // counts when the microphone hears the next correct note
  const handleNoteMatched = () => {
    setProgress((p) => Math.min(totalNotes, p + 1));
  };

  // counts when the on-screen piano advances
  const handleVPProgress = (index /* total, wasCorrect */) => {
    setProgress(Math.min(totalNotes, index));
  };

  const handleMelodyComplete = () => {
    if (!melodyCompleted) {
      setMelodyCompleted(true);
      setProgress(totalNotes); // 7/7
      setShowConfetti(true);
      setShowPanWis(true);
    }
  };

  const current = introSlides[currentSlide];

  const renderSlide = () => {
    switch (current.type) {
      case "story":
        return (
          <>
            <img
              src={current.image}
              alt="Story"
              className="w-full max-h-[400px] object-contain rounded-xl shadow-lg mb-4"
            />
            <p className="text-lg text-center max-w-3xl">{current.text}</p>
          </>
        );
      case "notes":
        return (
          <>
            <h2 className="text-blue-700 font-bold mb-3 text-lg text-center">🎼 White Notes</h2>
            <p className="text-center mb-3 text-gray-700">{current.text}</p>
            <div className="flex justify-center flex-wrap gap-2 mb-3">
              {basicNotes.map((n) => (
                <span
                  key={n}
                  className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full font-semibold shadow text-sm"
                >
                  {n}
                </span>
              ))}
            </div>
            <img
              src={current.image}
              alt="Piano Keys"
              className="w-full max-h-[300px] object-contain rounded-xl shadow-lg"
            />
          </>
        );
      case "octaveMap":
        return (
          <>
            <h2 className="text-blue-700 font-bold mb-3 text-lg text-center">
              🎹 Find the Middle Octave (C4 → C5)
            </h2>
            <p className="text-center mb-3 text-gray-700">{current.text}</p>
            <img
              src={current.image}
              alt="Octave Map (C4 to C5)"
              className="w-full max-h-[420px] object-contain rounded-xl shadow-lg"
              draggable="false"
            />
          </>
        );
      case "cta":
        return (
          <div className="w-full text-center">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-purple-700 tracking-wide leading-tight">
              Now use your own piano and play the seven white notes to unlock Chapter 1!
            </h2>
            <p className="mt-4 text-lg sm:text-xl text-gray-800">
              Play them in order:&nbsp;
              <span className="font-bold tracking-wider">C · D · E · F · G · A · B</span>
            </p>
            <p className="mt-2 text-sm text-gray-600">When you’re ready, scroll down to let us listen.</p>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-100 via-pink-100 to-yellow-100 text-gray-900 text-sm sm:text-base flex flex-col items-center p-4">
      <style>{`
        .intro-rpl button { font-size: 1.3rem; padding: 1.3rem 2rem; border-radius: 9999px; }
      `}</style>

      {showConfetti && <Confetti width={window.innerWidth} height={window.innerHeight} />}

      {/* Slide progress + narration controls */}
      <div className="w-full max-w-6xl mb-3 sticky top-0 z-10">
        <div className="h-2 bg-purple-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-purple-500 transition-all duration-500"
            style={{ width: `${((currentSlide + 1) / introSlides.length) * 100}%` }}
          />
        </div>

        {/* Narration toggle (default ON) and optional voice picker */}
        <div className="mt-2 flex items-center justify-end gap-2">
          <label className="flex items-center gap-2 text-xs sm:text-sm">
            <input
              type="checkbox"
              checked={narrationOn}
              onChange={(e) => {
                setNarrationOn(e.target.checked);
                if (e.target.checked) {
                  window.speechSynthesis?.cancel();
                  speak(narrationBySlide[currentSlide]);
                } else {
                  window.speechSynthesis?.cancel();
                }
              }}
            />
            <span className="text-purple-800 font-medium">🔊 Narration (UK English)</span>
          </label>
          {voices.length > 0 && (
            <select
              className="text-xs border rounded-md px-2 py-1"
              value={voiceIndex}
              onChange={(e) => setVoiceIndex(Number(e.target.value))}
              title="Choose voice"
            >
              {voices.map((v, i) => (
                <option key={v.name + i} value={i}>
                  {v.name} {v.lang ? `(${v.lang})` : ""}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      {/* Slides */}
      <div className="w-full max-w-6xl bg-white/90 rounded-xl shadow-xl p-8 mb-6 min-h-[60vh] flex flex-col items-center justify-center text-center">
        {renderSlide()}
        <div className="flex justify-center gap-4 mt-6">
          <button
            disabled={currentSlide === 0}
            onClick={() => setCurrentSlide((p) => Math.max(0, p - 1))}
            className="bg-purple-100 hover:bg-purple-200 text-purple-800 font-semibold px-4 py-2 rounded-full shadow disabled:opacity-30 text-sm"
          >
            ⬅️ Prev
          </button>
          <button
            disabled={isLastSlide}
            onClick={() => setCurrentSlide((p) => Math.min(introSlides.length - 1, p + 1))}
            className="bg-purple-100 hover:bg-purple-200 text-purple-800 font-semibold px-4 py-2 rounded-full shadow disabled:opacity-30 text-sm"
          >
            Next ➡️
          </button>
        </div>
      </div>

      {/* Listening section */}
      <section ref={listenSectionRef} className="w-full max-w-xl text-center mb-8 intro-rpl">
        <div className="text-sm text-gray-700 mb-3">
          <p>🎧 Play the 7 white notes on your real piano (C D E F G A B).</p>
          <p>🎧 We’ll listen and track your progress!</p>
          {micMsg && <p className="text-xs text-red-600 mt-2">{micMsg}</p>}
        </div>

        {micPrimed && (
          <RealPianoListener
            key={micPrimed ? "mic-primed" : "mic-unprimed"}
            targetMelody={basicNotes}
            onNoteMatched={handleNoteMatched}
            onComplete={handleMelodyComplete}
            onNoteDetected={setDetectedNote}
            startLabel="Start Listening"
          />
        )}

        {detectedNote && (
          <div className="inline-block mt-2 px-3 py-1 rounded-full bg-green-100 text-green-800 text-sm font-semibold">
            Detected: {detectedNote}
          </div>
        )}

        <img
          src="/images/Intro/octave_map.png"
          alt="Octave Map (C4 to C5)"
          className="mt-4 w-full max-w-[43rem] mx-auto rounded-xl shadow"
          draggable="false"
        />
      </section>

      {/* Optional game */}
      <div className="w-full max-w-6xl mb-8">
        <details className="bg-white/80 rounded-xl shadow p-4">
          <summary className="cursor-pointer font-semibold">🧩 Optional: Find the White Notes game</summary>
          <div className="mt-4">
            <FindTheNoteGame notes={basicNotes} onComplete={() => {}} />
          </div>
        </details>
      </div>

      {/* Virtual Piano — heading + hint (NO CARD / NO EXTRA BOX) */}
      <div className="w-full flex justify-center mb-6">
        <div className="w-full max-w-[1200px] mx-auto px-2 sm:px-4">
          <h3 className="text-center font-extrabold tracking-wide text-purple-700 mb-3 text-xl sm:text-2xl">
            🎹 NO PIANO? USE THE VIRTUAL PIANO BELOW!
          </h3>

          {showTiltHint && !hintDismissed && (
            <div className="mb-3 mx-auto max-w-[34rem] rounded-xl bg-purple-50 border border-purple-200 px-4 py-2 text-purple-800 text-sm flex items-center justify-between gap-3">
              <span>📱 Tip: For the full keyboard, <b>rotate your phone</b> to landscape — or <b>swipe sideways</b> in portrait.</span>
              <button
                onClick={() => setHintDismissed(true)}
                className="shrink-0 px-2 py-1 rounded-lg bg-purple-100 hover:bg-purple-200"
              >
                Got it
              </button>
            </div>
          )}

          {/* Tight viewport: height matches VirtualPiano's whiteKeyHeight (168px) */}
          <div className="overflow-hidden">
            <PianoViewport
              naturalWidth={1024}
              naturalHeight={168}
              desktopBreakpoint={1024}
              narrowPortrait={420}
            >
              <div className="flex justify-center">
                <VirtualPiano
                  targetMelody={melodyCompleted ? [] : basicNotes}
                  onProgress={handleVPProgress}
                  onComplete={handleMelodyComplete}
                  onNoteClick={(note) => noteClickHandlerRef.current?.(note)}
                  className="!bg-transparent !border-0 !shadow-none"
                />
              </div>
            </PianoViewport>
          </div>

          {/* Progress tracker under the piano */}
          <div className="mt-3 flex flex-col items-center gap-2">
            <div className="text-sm font-semibold text-purple-800">
              Progress: {progress}/{totalNotes}
            </div>
            <div className="flex flex-wrap justify-center gap-2">
              {basicNotes.map((n, i) => (
                <span
                  key={n + i}
                  className={
                    "px-3 py-1 rounded-full text-xs font-bold border " +
                    (i < progress
                      ? "bg-green-100 text-green-800 border-green-300"
                      : "bg-gray-100 text-gray-600 border-gray-300")
                  }
                >
                  {n}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Pan & Wis modal after success */}
      <PanWisIntroModal
        open={showPanWis}
        onClose={() => setShowPanWis(false)}
        onGoChapter1={() => navigate("/chapter-1")}
        onOpenMap={() => setMapOpen(true)}
      />

      {/* Clickable Overworld Map (Lightbox) */}
      <OverworldLightbox open={mapOpen} onClose={() => setMapOpen(false)} />

      {/* Footer */}
      <div className="text-xs text-gray-500 text-center mb-2">
        © 2025 Treble Town • Musical learning by Nishat Chadha
      </div>
    </div>
  );
}
